import { useEffect, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchableSelect } from "@/components/admin/SearchableSelect";
import { ProtocolEvidenceFields } from "@/components/equipment/ProtocolEvidenceFields";
import { Loader2 } from "lucide-react";

interface EvidenceDialogProps {
  open: boolean;
  title: string;
  description: string;
  submitLabel: string;
  saving: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (notes: string, files: File[]) => void;
  extra?: ReactNode;
  hidePhotos?: boolean;
  initialNotes?: string;
}

export function EquipmentEvidenceDialog({
  open,
  title,
  description,
  submitLabel,
  saving,
  onOpenChange,
  onSubmit,
  extra,
  hidePhotos = false,
  initialNotes = "",
}: EvidenceDialogProps) {
  const [notes, setNotes] = useState(initialNotes);
  const [files, setFiles] = useState<File[]>([]);

  useEffect(() => {
    if (open) {
      setNotes(initialNotes);
      setFiles([]);
    }
  }, [open, initialNotes]);

  const resetAndClose = (next: boolean) => {
    if (!next) {
      setNotes(initialNotes);
      setFiles([]);
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={resetAndClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {extra}
          <ProtocolEvidenceFields
            notes={notes}
            onNotesChange={setNotes}
            files={files}
            onFilesChange={setFiles}
            hidePhotos={hidePhotos}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => resetAndClose(false)} disabled={saving}>
            Anuluj
          </Button>
          <Button onClick={() => onSubmit(notes, files)} disabled={saving}>
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface AdHocItemFieldsProps {
  name: string;
  onNameChange: (value: string) => void;
}

export function AdHocItemFields({ name, onNameChange }: AdHocItemFieldsProps) {
  return (
    <div className="space-y-2">
      <Label htmlFor="adhoc-item-name">Nazwa przedmiotu</Label>
      <Input
        id="adhoc-item-name"
        value={name}
        onChange={(e) => onNameChange(e.target.value)}
        placeholder="np. Klucz do głównego wejścia"
      />
    </div>
  );
}

interface AssetHandoverFieldsProps {
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
}

export function AssetPickerField({ options, value, onChange }: AssetHandoverFieldsProps) {
  return (
    <div className="space-y-2">
      <Label>Narzędzie ze stanu firmy</Label>
      <SearchableSelect
        options={options}
        value={value}
        onValueChange={onChange}
        placeholder="Wybierz narzędzie..."
        searchPlaceholder="Szukaj narzędzia..."
        emptyText="Brak wolnych narzędzi"
      />
    </div>
  );
}

interface WorkerPickerFieldProps {
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
}

export function WorkerPickerField({ options, value, onChange }: WorkerPickerFieldProps) {
  return (
    <div className="space-y-2">
      <Label>Pracownik</Label>
      <SearchableSelect
        options={options}
        value={value}
        onValueChange={onChange}
        placeholder="Wybierz pracownika..."
        searchPlaceholder="Szukaj pracownika..."
        emptyText="Brak pracowników"
      />
    </div>
  );
}
